/**
 * "This Week" home-page widget (Round J-2, 2026-06-05).
 *
 * Three columns side-by-side on desktop, stacked on mobile:
 *   1. Calls       — DTD2 list this week + manual adds for the week
 *   2. Tasks       — open tasks (contact + transaction) due this week
 *   3. Appointments— open houses + signup-form events this week
 *
 * Daniel asked for a single morning-glance widget so he doesn't have to
 * tab between /contacts (the DTD2 list), /tasks, and /calendar. Each row
 * deep-links to the relevant detail page; the "See all" footer link
 * routes to the full bucket page.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  getThisWeek,
  type ThisWeekAppointment,
  type ThisWeekCall,
  type ThisWeekTask,
} from '../api/home';
import { formatPhone } from '../utils/phone';

function dayLabel(unix: number): string {
  const d = new Date(unix * 1000);
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  if (sameDay) return 'Today';
  // Tomorrow check.
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (
    d.getFullYear() === tomorrow.getFullYear() &&
    d.getMonth() === tomorrow.getMonth() &&
    d.getDate() === tomorrow.getDate()
  ) {
    return 'Tomorrow';
  }
  return d.toLocaleDateString(undefined, { weekday: 'short' });
}

function timeLabel(unix: number): string {
  return new Date(unix * 1000).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function ThisWeekCard() {
  const q = useQuery({ queryKey: ['home', 'this-week'], queryFn: getThisWeek });

  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: '24px',
        marginTop: 24,
      }}
    >
      <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2
          style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontSize: 17,
            fontWeight: 600,
            letterSpacing: '-0.01em',
          }}
        >
          This week
        </h2>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {q.data ? `Connected group ${q.data.dtd2_group}` : ' '}
        </div>
      </header>

      {q.isLoading && (
        <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</div>
      )}
      {q.error && (
        <div style={{ color: 'var(--accent-red, #B71C1C)', fontSize: 14 }}>
          Couldn't load this week. Refresh to try again.
        </div>
      )}

      {q.data && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 16,
          }}
        >
          <Column
            title="Calls"
            count={q.data.calls.length}
            seeAllTo="/contacts"
            empty="No one to call this week."
          >
            {q.data.calls.map((c) => (
              <CallRow key={c.id} call={c} />
            ))}
          </Column>

          <Column
            title="Tasks"
            count={q.data.tasks.length}
            seeAllTo="/tasks"
            empty="No tasks due this week."
          >
            {q.data.tasks.map((t) => (
              <TaskRow key={`${t.source}-${t.id}`} task={t} />
            ))}
          </Column>

          <Column
            title="Appointments"
            count={q.data.appointments.length}
            seeAllTo="/calendar"
            empty="No appointments this week."
          >
            {q.data.appointments.map((a) => (
              <ApptRow key={`${a.kind}-${a.id}`} appt={a} />
            ))}
          </Column>
        </div>
      )}
    </section>
  );
}

function Column({
  title,
  count,
  seeAllTo,
  empty,
  children,
}: {
  title: string;
  count: number;
  seeAllTo: string;
  empty: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-heading)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {title}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{count}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
        {count === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '8px 0' }}>{empty}</div>
        ) : (
          children
        )}
      </div>
      <Link
        to={seeAllTo}
        style={{
          marginTop: 12,
          fontSize: 12,
          color: 'var(--text-muted)',
          textDecoration: 'none',
        }}
      >
        See all →
      </Link>
    </div>
  );
}

function rowStyle(): React.CSSProperties {
  return {
    padding: '8px 10px',
    borderRadius: 'var(--radius-md, 6px)',
    fontSize: 13,
    color: 'var(--text-body)',
    textDecoration: 'none',
    display: 'block',
    transition: 'background 120ms',
  };
}

function CallRow({ call }: { call: ThisWeekCall }) {
  const name = `${call.first_name}${call.last_name ? ` ${call.last_name}` : ''}`;
  const phone = call.phone || call.secondary_phone;
  return (
    <Link
      to={`/contacts/${call.id}`}
      style={rowStyle()}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover, rgba(0,0,0,0.03))')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontWeight: 500 }}>{name}</span>
        {call.manual_added === 1 && (
          <span
            title="Added to this week manually, outside the alphabetical rotation"
            style={{ fontSize: 10, color: '#9A6E0A', background: '#FFF7E8', padding: '1px 6px', borderRadius: 4 }}
          >
            manual
          </span>
        )}
      </div>
      {phone && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{formatPhone(phone)}</div>
      )}
    </Link>
  );
}

function TaskRow({ task }: { task: ThisWeekTask }) {
  const to =
    task.source === 'contact' && task.contact_id
      ? `/contacts/${task.contact_id}`
      : task.transaction_id
        ? `/transactions/${task.transaction_id}`
        : '/tasks';
  return (
    <Link
      to={to}
      style={rowStyle()}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover, rgba(0,0,0,0.03))')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {task.title}
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {dayLabel(task.due_date)}
        </span>
      </div>
      {task.address && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {task.address}
        </div>
      )}
    </Link>
  );
}

function ApptRow({ appt }: { appt: ThisWeekAppointment }) {
  const to = appt.transaction_id ? `/transactions/${appt.transaction_id}` : '/calendar';
  const kindEmoji = appt.kind === 'open_house' ? '○' : '◇';
  return (
    <Link
      to={to}
      style={rowStyle()}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover, rgba(0,0,0,0.03))')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontWeight: 500 }}>
          <span style={{ color: 'var(--text-muted)', marginRight: 4 }}>{kindEmoji}</span>
          {appt.title}
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {dayLabel(appt.occurs_at)} · {timeLabel(appt.occurs_at)}
        </span>
      </div>
      {appt.address && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {appt.address}
        </div>
      )}
    </Link>
  );
}
